import React, { useMemo, useState } from "react"
import PropTypes from "prop-types"
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts"

const MAX_ITEMS = 10

const truncar = (s, n = 36) => (s && s.length > n ? `${s.slice(0, n - 1)}…` : s || "—")

const formatNumber = n => (typeof n === "number" ? n.toLocaleString("es-CO") : n ?? "—")

const colorParaGaps = gaps => {
  if (gaps === 0) return "#10b981"
  if (gaps < 100) return "#3b82f6"
  if (gaps < 1000) return "#f59e0b"
  return "#ef4444"
}

const TooltipContent = ({ active, payload }) => {
  if (!active || !payload || !payload[0]) return null
  const item = payload[0].payload
  return (
    <div
      style={{
        background: "#0f172a",
        color: "#fff",
        padding: "10px 14px",
        borderRadius: 8,
        fontSize: 12,
        boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
        maxWidth: 320,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 4, fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
        {item.prefijo}
      </div>
      {item.compartido && (
        <div style={{ fontSize: 11, color: "#fbbf24", marginBottom: 4 }}>
          <i className="mdi mdi-source-branch me-1" />
          Compartido entre {item.numSucursales} sucursales
        </div>
      )}
      <div style={{ color: "#fca5a5" }}>
        <i className="mdi mdi-alert-circle-outline me-1" />
        {formatNumber(item.gaps)} gaps
      </div>
      <div style={{ color: "#94a3b8" }}>{formatNumber(item.folios)} folios emitidos</div>
      {item.sucursales && (
        <div style={{ color: "#cbd5e1", marginTop: 4, fontSize: 11 }}>{item.sucursales}</div>
      )}
    </div>
  )
}

TooltipContent.propTypes = { active: PropTypes.bool, payload: PropTypes.array }

const GraficoTopPrefijos = ({ prefijos }) => {
  const [abierto, setAbierto] = useState(true)

  const datos = useMemo(() => {
    if (!prefijos) return []
    return prefijos
      .filter(p => (p.gaps_estimados ?? 0) > 0)
      .map(p => {
        const sucursalesLista = p.sucursales_lista || []
        return {
          prefijo: p.prefijo,
          prefijoCorto: truncar(p.prefijo, 14),
          gaps: p.gaps_estimados ?? 0,
          folios: p.total_folios ?? 0,
          compartido: Boolean(p.es_compartido),
          numSucursales: sucursalesLista.length,
          sucursales: sucursalesLista.join(" + "),
        }
      })
      .sort((a, b) => b.gaps - a.gaps)
      .slice(0, MAX_ITEMS)
  }, [prefijos])

  if (datos.length === 0) {
    return (
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          padding: 20,
          border: "1px solid #e2e8f0",
          textAlign: "center",
          color: "#10b981",
        }}
      >
        <i className="mdi mdi-check-circle-outline me-2" />
        No hay prefijos con gaps. ¡Todo en orden!
      </div>
    )
  }

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 14,
        padding: 20,
        border: "1px solid #e2e8f0",
      }}
    >
      <div
        className="d-flex justify-content-between align-items-center"
        style={{ cursor: "pointer" }}
        onClick={() => setAbierto(a => !a)}
      >
        <div>
          <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600 }}>
            <i className="mdi mdi-chart-bar me-2 text-primary" />
            Top {datos.length} prefijos con más gaps
          </h6>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
            Identifica dónde concentrar el trabajo de revisión
          </div>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-link text-muted p-0"
          style={{ fontSize: 18 }}
        >
          <i className={`mdi mdi-chevron-${abierto ? "up" : "down"}`} />
        </button>
      </div>

      {abierto && (
        <div style={{ marginTop: 16, width: "100%", height: Math.max(220, datos.length * 34) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={datos} layout="vertical" margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
              <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} />
              <YAxis
                type="category"
                dataKey="prefijoCorto"
                width={120}
                tick={{ fontSize: 12, fill: "#475569", fontFamily: "ui-monospace, SFMono-Regular, monospace" }}
                interval={0}
              />
              <Tooltip content={<TooltipContent />} cursor={{ fill: "rgba(59,130,246,0.08)" }} />
              <Bar dataKey="gaps" radius={[0, 6, 6, 0]}>
                {datos.map((d, i) => (
                  <Cell key={i} fill={colorParaGaps(d.gaps)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}

GraficoTopPrefijos.propTypes = {
  prefijos: PropTypes.array,
}

export default GraficoTopPrefijos
